// src/padplanner/padAlign.js
//
// "Align to pad" (S6b prerequisite 2): whether a person's grid and snap
// frame follow the pad (lib/footprint-editor/padAxis.js), and to what: the
// long side ({mode: "long"}) or a perimeter edge they clicked ({mode:
// "edge", ring, index}). Off by default. A view-and-snap aid: kept in
// localStorage per user per plan, never in the plan, a feature or a save.
// Storage can be missing or refuse: then the choice lasts as long as the
// page, and nothing throws (mapView.js does the same).
import { useCallback, useEffect, useState } from "react";
import { useAuthStore } from "../stores/useAuthStore";

export const padAlignKey = (userId, planId) => `aiprime.padplanner.padAlign.v1:${userId ?? "anonymous"}:${planId}`;

/** A stored value as a choice, or null (off, or not a choice). */
export function asChoice(v) {
  if (v?.mode === "long") return { mode: "long" };
  if (v?.mode === "edge" && Number.isInteger(v.ring) && v.ring >= 0 && Number.isInteger(v.index) && v.index >= 0) {
    return { mode: "edge", ring: v.ring, index: v.index };
  }
  return null;
}

export function readPadAlign(userId, planId) {
  try { return asChoice(JSON.parse(window.localStorage.getItem(padAlignKey(userId, planId)) ?? "null")); } catch { return null; }
}

export function writePadAlign(userId, planId, choice) {
  try {
    if (choice) window.localStorage.setItem(padAlignKey(userId, planId), JSON.stringify(choice));
    else window.localStorage.removeItem(padAlignKey(userId, planId));
  } catch { /* the page keeps it */ }
}

/** [choice, setChoice] for the signed-in user on one plan. */
export function usePadAlign(planId) {
  const userId = useAuthStore((s) => s.profile?.id ?? null);
  const [choice, set] = useState(() => readPadAlign(userId, planId));
  useEffect(() => { set(readPadAlign(userId, planId)); }, [userId, planId]);
  const setChoice = useCallback((c) => { const v = asChoice(c); writePadAlign(userId, planId, v); set(v); }, [userId, planId]);
  return [choice, setChoice];
}

/** The page's words for an axis: "Pad axis 23.0° (long side)". */
export function axisText(axis) {
  if (!axis) return null;
  const src = axis.source === "edge" ? "picked edge" : axis.nearSquare ? "long side; near-square, either side gives the same grid" : "long side";
  return `Pad axis ${axis.deg.toFixed(1)}° (${src})`;
}
